// Initialises main campaign entry forms and binds listeners for validation etc

import * as formUtil from '../utils/formUtils';
import * as Browser from '../utils/browser';
import { hashCode, video } from '../utils/utils';
import Overlay from './Overlay';
import { verifyHuman } from '../utils/formUtils';
import { createUpload } from '@mux/upchunk';
import _forEach from 'lodash/forEach';

import { ngxValidators, compositeValidators } from '../utils/FormValidations';
import { AnalyticsEvents as PUB, Events as PRIV, DeprecatedAnalyticsEvents as OLD } from '../../common/config/api';
import enums from '../../common/config/enums';
import { getCurrentCampaignDateTime, isTimeBetween } from '../utils/timeUtils';

var _cntx = NGX.context;

var PROGRESSIVE_FILTERING_KEY = 'ngx.progressive';

/**
 * Construct ajax redirect URL
 * @param {string} url - base URL
 * @param {object} params
 * @returns {string}
 */
var getRedirectURL = function(url, params) {
  var _redirectUrl = url,
    _paramsToSend = {},
    _ngxMappingObject = window.ngxMappingObject || {};

  // Mapping function for SNI data mart
  _.each(_ngxMappingObject, function(v, k) {
    if (params.data && params.data.hasOwnProperty(v)) {
      _paramsToSend['nc.' + k] = encodeURIComponent(params.data[v]);
    }
  });

  // If there's a quiz result, add the payload
  if (params.quiz && params.quiz.hasOwnProperty('payload') && params.quiz.payload) {
    _paramsToSend.payload = params.quiz.payload;
  }

  // If there's a coupon, add the payload
  if (params.coupon && params.coupon.hasOwnProperty('payload') && params.coupon.payload) {
    _paramsToSend.cpayload = params.coupon.payload;
  }

  // Confirmation pages are looking for 'containerId' param in order to generate a
  // displayContainer object to pass to the view model.
  if (NGX.context.displayContainer) {
    // WYN-6615. Adding the "responsive" parameter into the request. Otherwise, in case of usage of custom domain,
    // the parent iframe will not be resized
    if (NGX.context.displayContainer.responsive)
      _paramsToSend.mode = 'responsive';
    _paramsToSend.containerId = NGX.context.displayContainer.guid;
  }
  return Browser.updateQueryString(_paramsToSend, false, _redirectUrl);
};

/**
 * Inner function to handle scrolling (to ensure that the current item in the combobox is visible)
 * @param {*} activeElement
 * @param {*} scrollParent
 */
function maintainScrollVisibility(activeElement, scrollParent) {
  const { offsetHeight, offsetTop } = activeElement;
  const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

  const isAbove = offsetTop < scrollTop;
  const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

  if (isAbove) {
    scrollParent.scrollTo(0, offsetTop);
  } else if (isBelow) {
    scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
  }
};

/**
 * This function "makes" the ScreenReader to say something. Used in comboboxes.
 * It is ugly, but we don't have any other ways here. Another way is to use plugins - see
 * https://github.com/selectize/selectize.js/issues/798#issuecomment-371329537
 * @param {*} text Text which will be said
 * @param {*} priority "polite"/"assertive"
 */
function screenReaderSpeak(text, priority = "polite") {
  if (!!text) {
    let el = document.createElement("div");
    let id = "speak-" + Date.now();
    el.setAttribute("id", id);
    el.setAttribute("aria-live", priority);
    el.classList.add("visually-hidden");
    // Hiding this element (but we need to add it into DOM to make the ScreenReader to announce the text passed)
    el.style.position = 'absolute';
    el.style.left = '-100%';
    document.body.append(el);

    window.setTimeout(function () {
      document.getElementById(id).innerHTML = text;
    }, 100);

    window.setTimeout(function () {
        document.body.removeChild(document.getElementById(id));
    }, 1000);
  }
}

/**
 * Applies styling to combo/select boxes
 * Currently via Selectize
 */
var initialiseSelectBoxes = function(form) {

  if (!Browser.isMobile() && !NGX.entryForm.basicMode) {
    log.debug('Initialising select boxes', 6);

    const selectFields = $('select', form);

    selectFields.each( function(selectIndex, selectElement) {
      // Remembering the ID of the SELECT element (for future usage)
      const selectElementId = selectElement.id;
      // Link to the combobox element (the container with options)
      let comboboxElement;
      // Remembering the corresponding input element
      let inputElement;

      $(selectElement).selectize({
        wrapperClass:         'xComboWrapper',
        inputClass:           'xComboInput',
        dropdownClass:        'xComboOptions',
        dropdownContentClass: 'xComboOption',
        selectOnTab:          true,
        allowEmptyOption:     true,
        maxOptions:           2000,
        render:               {
          option: function(item, escape) {
            return (
              `<div id="${selectElementId}-${item.$order}" class="option" role="option" data-value="${item.value}">${escape(item.text)}</div>`
            );
          },
        },
        // Handler on DropDown open. Trying to set the correct scroll position
        onDropdownOpen:       function() {
          inputElement.attr('aria-expanded', true);
          inputElement.attr('tabindex', '1');
          this.$control.attr('changed', 'false');
          if (this.items[0] || this.items[0] === '') {
            for (let key of Object.keys(this.options)) {
              if (this.options[key].value === this.items[0]) {
                // Setting the "aria-selected" attribute for the option selected by default
                if (!!this.items[0])
                  $(`.xComboOption [role=option][data-value="${this.items[0]}"]`, comboboxElement)[0].setAttribute('aria-selected', true);
                break;
              }
            }
          }
        },
        onDropdownClose:      function(...args) {
          inputElement.attr('tabindex', '0');
          inputElement.attr('aria-expanded', false);
          inputElement.attr('aria-activedescendant', '');
          this.$control.attr('data-expanded', false);
        },
        onChange:             function(value) {
          // Removing the aria-selected attr from the previously selected option
          $(`.xComboOption [role=option][aria-selected=true]`, comboboxElement)[0]?.removeAttribute('aria-selected');
          for (let key of Object.keys(this.options)) {
            if (this.options[key].value === value) {
              this.$control.attr('selectedValue', value);
              this.$control_input.attr('aria-activedescendant', '');
              // Checking if we are in the input of the current combobox. If yes, we need
              // to sound the selected value
              setTimeout( () => {
                if (this.$control_input[0] && this.$control_input[0] === document.activeElement) {
                  screenReaderSpeak(this.options[key].text + ' selected', 'assertive');
                }
              }, 0)
              break;
            }
          }

          if (!!value)
            $(`.xComboOption [data-value="${value}"]`, comboboxElement)[0].setAttribute('aria-selected', true);
        },
        onInitialize:         function() {
          comboboxElement = $('.xComboOptions', this.$control.closest('.xComboWrapper'));
          comboboxElement.attr('role', 'listbox')
          // Adding ID for the combobox (container with possible options)
          comboboxElement.attr('id', `${selectElementId}_Listbox`);

          // API blur and focus events don't want to register for this input
          // generated by selectize.
          // Finding the input element to add appropriate ARIA attributes
          inputElement = this.$control_input;
          inputElement.data('apiIgnore', selectElementId);
          inputElement.attr('autocomplete', 'none');
          inputElement.on('focus', (event) => {
            // If the user removes all from input - we can receive an undefined here
            if ($('div.item', this.$control)[0])
              screenReaderSpeak($('div.item', this.$control)[0].innerText);
          });

          inputElement.on('input', (event) => {

            const selectedOption = $('.xComboOption [aria-selected="true"]', comboboxElement)[0];
            let currentOption = '';
            setTimeout( () => {
              if (this.currentResults.total > 0) {
                this.$control.attr('data-expanded', 'true');
                const activeOption = $('.xComboOption .active', comboboxElement)[0];
                currentOption = activeOption.innerText;
              }
              screenReaderSpeak(currentOption + ' ' + this.currentResults.total + 'items available');
            }, 150);
            if (!event.target.value) {
              !!selectedOption && selectedOption.removeAttribute('aria-selected');
            }
          });

          inputElement.on('change', (event) => {
            if (!event.target.value && this.$control.attr('changed') == 'false') {
              this.clear();
            }
          });

          // Adding WAI-ARIA attributes
          // The "label" attribute. We make an attempt to take it from placeholder. If it is absent, then
          // we take the "title". And we add a "." here, to because of issue with autocompletes
          inputElement.attr('aria-label', selectElement.getAttribute('placeholder') || selectElement.getAttribute('title'));
          inputElement.attr('aria-haspopup', 'listbox');
          inputElement.attr('aria-expanded', false);
          inputElement.attr('role', 'combobox');
          inputElement.attr('tabindex', '0');
          inputElement.attr('aria-controls', `${selectElementId}_Listbox`);

          this.$control.on('keydown', (event) => {
            // We use isExpanded here. This UGLY construction is used to ensure that the current active element in
            // combobox will be correctly sounded by the screenReader.
            // Why don't we use 'aria-expanded'? Because the onDropDownOpen handler happens BEFORE this handler.
            // And in case when we are on this field, and press "ArrowDown" key, the screenReader will sound incorrect
            // option (next by the current one).
            const isExpanded = this.$control.attr('data-expanded');
            if (event.keyCode === 13) {
              this.$control.attr('changed', 'true');
            }
            if ([38,40].includes(event.keyCode)) {
              if (isExpanded === 'true') {

                let options = $('.xComboOption [role=option]', comboboxElement);

                let selectedItemIndex = 0;
                do {
                  if (options[selectedItemIndex] && options[selectedItemIndex].classList.contains('active')) {
                    break;
                  }
                  selectedItemIndex++;
                }
                while(selectedItemIndex < options.length);

                this.$control_input.attr('aria-activedescendant', options[selectedItemIndex].id);
                // Ensuring that the item is visible
                maintainScrollVisibility(
                  options[selectedItemIndex],
                  comboboxElement.get(0)
                );
              }
              else {
                if (event.keyCode === 40) {
                  this.$control.attr('data-expanded', 'true');
                }
              }
            }
            return true;
          });
        },
        onType: function(str) {
          /** This callback is written for improving the accessibility, and fixing the problem
           * with input. When we navigate into the input, and start typing, we need to correctly set
           * the active-descendant attr. Otherwise, options will not be
           * correctly announced by the screenReader
           */
          let options = $('.xComboOption [role=option]', comboboxElement);
          let currentIndex = 0;
          for (let option of options) {
            if (option.dataset.value === this.$control.attr('selectedValue')) {
              this.$control_input.attr('aria-activedescendant', option.id);
              break;
            }
            currentIndex++;
          }
          if (currentIndex === options.length) currentIndex = 0;
        },
        onFocus: function(a) {
          const selectedOption = $('option', this.$input[0]);
          const selectedValue = selectedOption.text();
          screenReaderSpeak(this.$input.attr('title'), 'assertive');
          if (!!selectedValue) {
            setTimeout( () => {
              screenReaderSpeak(selectedValue + ' selected', 'assertive');
            }, 0);
          }

          this.$control.attr('data-expanded', true);
          const moduleId = findParent(this.$input[0], '.xFieldItem').id.replace(/Wrapper$/, '');
          if (NGX.App.config.userHasInteracted[moduleId]) {
            return;
          }
          NGX.Analytics.trigger(PUB.FORM_MODULE_INTERACTION, {
            remoteFieldName: moduleId,
          });
        },
      });

    });

  }

  if (Browser.isIE() || Browser.isEdge()) {
    $('input', form).mousedown(function() {
      $(this).focus();
    });
  }
};

/**
 * Applies uniform styling to form elements if not in basic mode
 * @param {node} form
 */

var initialiseFormStyling = function(parent = '#xCampaignForm', config = window.uniConfig) {
  if (!NGX.entryForm.basicMode) {
    /** WYN-6505. Sometimes we have the situation when we have 2 forms with the same ID in the document. To solve this,
     * we introduced this ugly hack. It is quick solution to make it work, but honestly we need to solve the root
     * of the problem
     */
    // Trying to find items with the same ID as the "parent" argument has
    const sameId = $(parent).attr('id');
    const sameIds = document.querySelectorAll(`#${sameId}`);
    if (sameIds.length > 0) {
      for (const sameParent of sameIds)
        $('textarea,#uploadFBVidInput, .textField, .checkField, .radioField, .uploadField', sameParent).uniform(config);
    }
    // If there are no items with the ID (or if the "parent" argument does not have ID attribute)
    else
      $('textarea,#uploadFBVidInput, .textField, .checkField, .radioField, .uploadField', parent).uniform(config);
  }

  updateUploadPlaceholders();
};

/**
 * Overrides Uniform's default placeholders for uploads if user has supplied an override
 */
var updateUploadPlaceholders = function() {
  var fieldsToUpdateSelectors = ['#ngxUserUpload', '#ngxUserProfile'];
  fieldsToUpdateSelectors.forEach(function(fieldSelector) {
    var field = $(fieldSelector);
    if (field && field.attr('type') === 'file') {
      var placeholder = field.attr('placeholder');
      if (placeholder) {
        $(field)
          .siblings('.xFilename')
          .text(placeholder);
      }
    }
  });
};

/**
 * Raty rebuilds parts of the DOM, which make it difficult to capture click
 * events. Using the build in callback (generated in FormsTagLib.groovy), we
 * can do a better job of customising it here.
 */
var initRatyRedirect = function() {
  NGX.App.Events.on('form:rating:click', function(e) {
    const wrapper = findParent(document.getElementById(e.id), '.xControlRating');
    if (!wrapper) {
      return;
    }
    const params = {
      experienceId: NGX.info.id,
    };
    NGX.Analytics.trigger(OLD.FORM_RATING_CLICK);

    params.remoteFieldName = wrapper.id.replace(/Wrapper$/, '');
    NGX.App.Events.trigger(PRIV.FORM_FIELD_FOCUS, params);

    params.value = e.value;
    NGX.Analytics.trigger(PUB.FORM_MODULE_INTERACTION, params);
    NGX.App.Events.trigger(PRIV.FORM_FIELD_BLUR, params);
  });
};

/**
 * WYN-4109 Configures any progressive fields before form is displayed.
 */
var initialiseProgressiveFields = function(form, email) {

  const allowCookies = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.FUNCTIONAL);
  if (!allowCookies) {
    // WYN-6333 Privacy consent - If we do not allow functional cookies, then we simply skip this behavior for progressive
    // fields.  We will not store user choices, nor will we check if they were previously answered or hide them.
    return;
  }

  // Retrieve progressive store from localstorage and parse to native object.
  var store = NGX.StorageManager.get(PROGRESSIVE_FILTERING_KEY) || '{}';
  store = JSON.parse(store);

  // The store root object.
  var storeRoot = null;

  // The campaigns id.
  var campaignId = NGX.App.config.id;

  // Resolve the stores primary key.
  if (email) {
    // If remember me is enabled and a user email is supplied, this
    // will be a hash of their email.
    var emailHash = hashCode(email);
    if (!store[emailHash]) {
      store[emailHash] = {};
    }
    storeRoot = store[emailHash];
    // Otherwise it will be store root.
  } else {
    storeRoot = store;
  }

  // Initialize the campaign key if required.
  if (!storeRoot[campaignId]) {
    storeRoot[campaignId] = {};
  }

  // Find all progressive fields.
  $(form)
    .find('.xFieldItem[data-ngx-progressive="true"]')
    .each(function(index, element) {
      // Boolean to indicate state of current field.
      var isAnswered = false;

      // Get the fields id.
      var id = $(element).attr('data-ngx-id');

      // Hash the id before retrieving.
      var idHash = hashCode(id);

      // Get the fields scope.
      var scope = $(element).attr('data-ngx-progressive-scope');

      // Based on the scope, behave accordingly.
      switch (scope) {
        // Campaign only scope.
        case 'campaign':
          // If we have something in the store for this campaign id+field id.
          // (this will check for existing keys before we introduced hashing)
          if (storeRoot[campaignId][id]) {
            // If a (answered) prop is set and true.
            if (storeRoot[campaignId][id].a === 1) {
              // Set boolean.
              isAnswered = true;
            }
          }
          // (this will check for the new version after we introduced hashing)
          if (storeRoot[campaignId][idHash]) {
            // If a (answered) prop is set and true.
            if (storeRoot[campaignId][idHash].a === 1) {
              // Set boolean.
              isAnswered = true;
            }
          }
          break;

        // Any campaign.
        case 'all':
          // (TODO phase 2 dev).
          // Iterate over all campaign keys and look for matching field id.
          break;
      }

      // Finally, if question has already been answered, append the corresponding class and data attribute so
      // that it can be hidden and processed by the rest of the form logic correctly.
      if (isAnswered) {
        $(element).attr('data-ngx-progressive-answered', true);
        $(element).addClass('xProgressiveAnswered xHidden');

        // Disable all child inputs.
        $(element)
          .find('input, select')
          .each(function(fieldIndex, fieldElement) {
            $(fieldElement).attr('disabled', 'disabled');
          });
      }
    });
};

// Check for an external customer ID and id type passed in via a query string.  It should be
// in string format (the api checks it) and it should be prefixed with `nc.` so that
// it skips the CDN caching.
//
// We need to do this in EntryForm so that the form exists when we want to add the
// hidden field for the external customer id and type.
const initialiseKnownUser = function() {
  const custId = NGX.Browser.queryStringObject('nc.customerid');
  const custIdType = NGX.Browser.queryStringObject('nc.customeridtype');

  const decodedCustId = custId ? decodeURI(custId) : '';
  const decodedCustIdType = custIdType ? decodeURI(custIdType) : undefined; // type is optional
  // If they've set a type but not an id, don't do anything.
  if (decodedCustId && decodedCustId.length > 0) {
    NGX.App.api.setCustomerID(decodedCustId, decodedCustIdType);
  }

  // If the customer forgets the 'nc.', give them a reminder.
  if (NGX.Browser.queryStringObject('customerid')) {
    log.debug('The \'customerid\' query string parameter must contain the prefix \'nc.\', like this \'nc.customerid\'.');
  }
  // If the customer forgets the 'nc.', give them a reminder.
  if (NGX.Browser.queryStringObject('customeridtype')) {
    log.debug(
      'The \'customeridtype\' query string parameter must contain the prefix \'nc.\', like this \'nc.customeridtype\'.'
    );
  }
};

/** WYN-6505. In case ov reveal promotion campaign we can have 2 forms in the HTML layout with the same IDs and same field names.
 * To fix the validation problem caused by parsley lib, we override the getVal() function for checkboxes and radios.
 * For ddeply understanding see the original getVal implementation in parsley lib for ParsleyFieldMultiple class
 */
function _parsleyGetValOverride() {
  if ( this.isRadio ) {
    return $( this.siblings + ':checked:visible' ).val() || '';
  }

  if ( this.isCheckbox ) {
    var values = [];

    $( this.siblings + ':checked:visible' ).each( function () {
      values.push( $( this ).val() );
    } );

    return values;
  }
}

/**
 * Applies validation functionality
 * Currently via Parsley
 */
var initialiseValidation = function(form) {
  log.debug('Initialising form validation', 6);
  if (!form) return;

  const $form = $(form);

  // Disable HTML5 validation for JS enabled browsers [NGX-2982]
  form.setAttribute('novalidate', 'novalidate');

  $form.parsley({
    namespace:           'data-parsley-',
    excluded:            'input[type=hidden], :disabled',
    // add custom composite field to parsley validations
    inputs:              '[data-parsley-trigger]',
    useHtml5Constraints: false,
    validateIfUnchanged: true,
    validate:            false,
    validators:          ngxValidators,
    // Default validation messages
    messages:            {
      name:       'You must complete all fields',
      fileupload: 'The upload field is not populated',
      required:   '', // We don't want a default, if the user sets their required message to empty, let it be empty.
    },

    // validation classes
    successClass:   'xFieldValid',
    errorClass:     'xFieldError',
    validatedClass: 'xFieldValidated',
    // built in Parsely scrolling/focus was interfering with the scrolling done in formUtils:validate() method.
    focus:          'none',
    errors:         {
      // Override the class handler to apply classes to parent element
      classHandler: function(element) {
        var $el = $(element),
          $fieldItem = $el.parents('.xFieldItem');

        if ($fieldItem.hasClass('xControlAddress') && !$fieldItem.hasClass('xComposite')) {
          return $el.parents('.xFieldWrapAddress');
        }
        return $fieldItem;
      },
      errorElem: '<li style="list-style:none"></li>', // each field constraint fail in an li
    },
    listeners: {

      // WYN-6473 Debounce wrapper to ignore unuseful handlers of onError events in form validation.
      onFieldError: _.debounce(function(elem, constraints, parsleyfield) {
        elem.attr('aria-invalid', 'true');
        $(elem).attr('aria-describedby', parsleyfield.hash);
        if ($('.xFormValidated', form).length != 0) {
          $('.xSubmitContainer, .xActivateContainer', form).addClass('xFieldError');
        }

        // There's an annoying bug that shows two parsley errors simultaneously when you have a
        // required field, that you've typed an invalid value in and then erased it.  It shows both
        // the invalid and required errors.  This bit of code will remove the invalid error message
        // when the field is required, has no value, and has a type constraint.
        var $elem = $(elem);
        if (!!parsleyfield.constraints.required && $elem.val() === '' && !!$elem.data('parsley-type')) {
          parsleyfield.UI.ulTemplate.find('li:not(.required)').remove();
        }

        // Resize the form wrapper and iframe
        setTimeout(
          function() {
            formUtil.formWrapperResize($form);
          },
          100,
          $form,
          elem
        );
      }, 300),
      onFieldSuccess: function(elem, constraints, parsleyfield) {
        elem.attr('aria-invalid', 'false');
        $(elem).removeAttr('aria-describedby', parsleyfield.hash);
        if ($('.xFieldItem.xFieldError', form).length === 1) {
          $('.xSubmitContainer, .xActivateContainer', form).removeClass('xFieldError');
        }
      },
    },
  });

  // Initialise Uniform form styling
  initialiseFormStyling(form);

  // Ratings component validation
  initRatingValidation(form);

  // Composite validation
  initCompositeValidation(form);

  // Minimum age (dob) validation
  initMinimumAge(form);

  // Redirect Raty generated click events
  initRatyRedirect();

  // Check for and initialize a known user external customer id
  initialiseKnownUser();

  if (!form.dataset.validationInitialized) {

    /** WYN-6505. Overriding the getVal function for checkboxes and radios
     */
    const parsleyForm = $form.parsley();
    parsleyForm.items.forEach( i => {
      if (i.constructor.name === 'ParsleyFieldMultiple' &&
              (i.$element.is( 'input[type=checkbox]' ) || i.$element.is( 'input[type=radio]' ))) {
        i.getVal = _parsleyGetValOverride;
      };
    });

    /**
     * Event Binding and API updates
     */
    $form.parsley('addListener', {

      onFieldValidate: function(elem) {

        var $elem = $(elem);
        // 1. If the item is selectized, see if it's on screen first
        if ($elem.hasClass('selectized')) {
          return !$elem.parent().is(':visible');
        } else {
          // otherwise, if it's not even visible, don't validate
          return !$elem.is(':visible');
        }
      },
    });

    // catch form submission and apply parsley validation
    $form.submit(function(e) {
      e.preventDefault();
      if (!_cntx.isPreview) {
        NGX.entryForm.submitCampaignObjectForm(form);
      } else {
        NGX.Util.previewWarning();
      }
      return false;
    });

    /** WYN-7207. An ugly hack to avoid useless validations for EACH input on email fields.
     * Possibly, it would be better to apply such approach (not exact!) for all fields which require
     * particular input format (phones, addresses, etc.)
     * I did not find any event handlers for "onChange/onInput" events in Parsley 1.x docs, and
     * did not find any way to reset the validation for particular field.
     */
    $('input[type=email]', $form).each( (idx, elem) => {
      // When the user enters something we remove the constraint which checks the correct email
      $(elem).on('input', (e) => {
        $(elem).parsley( 'removeConstraint', 'type' );
      } );
      // When user leaves the field - we return validation
      $(elem).on('focusout', (e) => {
        $(elem).parsley('addConstraint', {type: 'email'} );
      } );
    } );

    form.dataset.validationInitialized = true;
  }
};

/**
 * Raty doesn't support validation out of the box, so applying here
 * @param {jQuery element} form
 */

var initRatingValidation = function(form) {
  $('.xControlRating.xRequired', form).each(function(index, elem) {
    var $el = $(elem),
      obj = NGX.entryForm.validations.rating;

    var err = $($el.attr('data-parsley-error-container')),
      fieldErrorMsg = $el.attr('data-ngx-control-error-msg');

    var objValidation = function(arg) {
      // Bypass on non-visible pages
      if (!$el.closest('.xFormPage').hasClass('cycle-slide-active')) {
        return true;
      }

      var $input = $('input', $el);
      if (!$input.val()) {
        $el.addClass('xFieldError');
        err.text(fieldErrorMsg || obj.msg || 'Invalid field');
        formUtil.formWrapperResize();
        return false;
      } else {
        $el.removeClass('xFieldError');
        return true;
      }
    };

    // bind fn to the change event of any child inputs.
    // if the wrapper has a data-trigger attr use those values
    $el.on($el.attr('data-parsley-trigger') || 'click', 'i,img', objValidation.bind($el));

    // populate the submission validation array
    NGX.entryForm.fieldGroups.push(objValidation.bind($el));
  });
};

function stripFieldName(fieldName) {
  return fieldName.slice(fieldName.lastIndexOf('_') + 1).toLowerCase();
}

// IE11: requires a polyfill for matches
if (!Element.prototype.matches) {
  Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
}

function findParent(elem, selectorString) {
  var current = elem;
  while (current !== document.body) {
    current = current.parentElement;
    if (current.matches(selectorString)) {
      return current;
    }
  }
  return;
}

/**
 * Trigger focus and blur events on all form fields for API
 *
 * This works as follows:
 *
 * 1. Find all the modules, figure out if they're composites (i.e. have more than 1 input field)
 * 2. Build up props to pass on the analytics bus from the DOM (we don't have a model with all of these meta-data, so
 *    I used the DOM to pull it out.
 * 3. For each field:
 *    a. tweak the 'focus', 'blur' and 'input' events base on the module type (some require all 3 to be called on
 *       'change').
 *    b. tweak the value returned from an event (composites need all field values)
 *    c. change the Node we capture metadata from (some fields simulate and update actual input / select Nodes. Use the
 *       actual nodes to build remoteFieldName, etc...).
 *    d. check for and add `data-ngx-api-bound` to avoid duplicate event binding and check for 'data-api-ignore' for
 *       a special case caused by the Selectizer library.
 *    e. bind: 'form:field:{focus,blur}' and 'form:module:interaction'
 *
 * @param {Element} form
 * @param {String} [optional] focus on a specific field item is supplied (instead of all)
 */
var bindAPItriggers = function(form, rootSelector) {
  const $form = $(form);

  const rootLookup = rootSelector || '.xFieldItem';
  const fieldLookup = 'input:not([type="hidden"]),select,textarea';

  // Event handlers will need meta information about the fields to support an API similar to
  // MX. Hold onto your butts, this isn't pretty.
  $form.find(rootLookup).each(function(index, itemParent) {
    // Address does not include xComposite if validation is set to distinct.
    const isComposite =
      itemParent.classList.contains('xComposite') ||
      (typeof itemParent.dataset.ngxControl === 'string' && itemParent.dataset.ngxControl.startsWith('address'));

    let buildProps = function emptyProps() {
      return {};
    };

    if (isComposite) {
      // Call to dynamically rebuild the current field values in a composite field
      buildProps = function(e) {
        const fieldContainer = findParent(e.target, '.xFieldContainer');
        if (!fieldContainer) {
          return {};
        }
        let fields = {};
        _forEach(fieldContainer.querySelectorAll(fieldLookup), function(elem) {
          if (elem.type === 'checkbox') {
            if (elem.checked) {
              fields = ($.isArray(fields) ? fields : []).concat(elem.value);
            }
          } else if (elem.name.length > 0) {
            fields[stripFieldName(elem.name)] = elem.value;
          }
        });
        return { fields: fields };
      };
    }

    const obj = {
      experienceId: NGX.info.id,
    };

    /**
     * Does the itemParent match a module type?
     *
     * @param {Node} parent - module container
     * @param {string} name - expected module type
     * @return {boolean}
     */
    const isModule = function(parent, name) {
      return parent.classList.contains('xControl' + name);
    }.bind(null, itemParent);

    const moduleId = itemParent.id;

    /**
     * Mutates the param to add a fieldName if necessary.
     *
     * @param {object} param - parameters being sent back to the user
     * @param {string} id - containing the pre/postfixed remoteFieldName
     * @return {object}
     */
    const updateFieldName = function(isComposite, param, target) {
      var mark = target.id.lastIndexOf('_');
      if (mark !== -1 && isComposite) {
        param.sourceField = target.id.slice(mark + 1).toLowerCase();
        param.remoteFieldName = target.id.slice(0, mark).toLowerCase();
      } else {
        param.remoteFieldName = target.id;
      }
      return param;
    }.bind(null, isComposite);

    // Customise events and values returned for each module here:
    _forEach(itemParent.querySelectorAll(fieldLookup), function(item) {
      let focus = 'focus';
      let blur = 'blur';
      let input = 'input';
      let value = function(e) {
        return e.target.value;
      };
      let targetFromEvent = function(e) {
        return e.target;
      };

      // Default behaviour
      if (item.nodeName === 'INPUT' && item.type === 'checkbox') {
        blur = focus = input = 'change';
        value = function(e) {
          return e.target.checked ? e.target.value : '';
        };
      }

      if (item.nodeName === 'SELECT' || isModule('Time') || isModule('Date')) {
        blur = 'change';
        input = 'focus';
      }

      // Module behaviour exceptions
      if (isModule('Upload') || isModule('Challengequestion') || isModule('Userprofileupload') || isModule('Radio')) {
        blur = focus = input = 'change';
      }

      if (isModule('Marketing') || isModule('Terms_and_conditions')) {
        value = function(e) {
          return e.target.checked;
        };
      }

      if (isModule('Multioptin') || isModule('Checkbox')) {
        value = function(e) {
          const fieldContainer = findParent(e.target, '.xFieldContainer');
          if (!fieldContainer) {
            return '';
          }
          const selected = [];
          _forEach(fieldContainer.querySelectorAll(fieldLookup), function(elem) {
            if (elem.checked) {
              selected.push(elem.value);
            }
          });
          return selected.join(',');
        };
      }

      if (isModule('Name')) {
        value = function(e) {
          const f = buildProps(e).fields;
          return [f.title, f.firstname, f.lastname].filter(field => field).join(' ');
        };
      }

      if (isModule('Address')) {
        value = function(e) {
          const f = buildProps(e).fields;
          return [f.address1, f.address2, f.city, f.country, f.zip]
            .filter(function(field) {
              return field;
            })
            .map(function(field) {
              return field.trim();
            })
            .join(',');
        };
      }

      _forEach(['Address', 'Country', 'Date', 'Filter'], function(moduleType) {
        if (isModule(moduleType)) {
          targetFromEvent = function(e) {
            return findParent(e.target, '.xField').querySelector('select, input');
          };
        }
      });

      _forEach(['Challengequestion', 'Checkbox', 'Choice', 'Multioptin', 'Radio', 'Select'], function(moduleType) {
        if (isModule(moduleType)) {
          targetFromEvent = function(e) {
            return {
              id: findParent(e.target, '.xControl' + moduleType).id.replace(/Wrapper$/, ''),
            };
          };
        }
      });

      // bindAPItriggers will get called multiple times, which is beyond our control. Avoid
      // duplicate event handlers by marking a node once it has been bound. Clunky but robust.
      if (item.dataset.ngxApiBound) {
        return;
      }
      item.dataset.ngxApiBound = true;

      $(item).on(focus, function formInputFocus(e) {
        var params = updateFieldName(Object.assign({}, obj), targetFromEvent(e));
        NGX.App.Events.trigger(PRIV.FORM_FIELD_FOCUS, params);
      });

      // Selectizer adds a hidden input, which causes event echoing with
      // empty values. Ignore these.
      if (!item.dataset.apiIgnore) {
        $(item).on(input, function formInputInteraction(e) {
          // The interaction is at the module level, and not the remoteFieldName level. Should trigger
          // once for a name module, and not for each {title,first,last}name input.
          if (!NGX.App.config.userHasInteracted[moduleId]) {
            var params = updateFieldName({}, targetFromEvent(e));
            NGX.Analytics.trigger(PUB.FORM_MODULE_INTERACTION, {
              remoteFieldName: params.remoteFieldName,
            });
          }
        });

        $(item).on(blur, function formInputBlur(e) {
          var params = Object.assign({}, obj, { value: value(e) }, buildProps(e));
          params = updateFieldName(params, targetFromEvent(e));
          NGX.App.Events.trigger(PRIV.FORM_FIELD_BLUR, params);
        });
      }
    });
  });
};

/**
 * Parsley doesn't support validating composite fields out of the box
 * @param {Dom element} form
 */
var initCompositeValidation = function(form) {
  log.debug('Initialising Composite Validations', 6);

  const $form = $(form);

  bindAPItriggers(form);

  $form.find('.xComposite[data-ngx-control]').each(function(index, item) {
    // get their matching validation fn
    var $el = $(item),
      $input = $('select[data-ngx-validtype],input:not([type="hidden"])', $el),
      type = $input.eq(0).data('ngxValidtype'),
      control = $el.data('ngxControl'),
      obj = NGX.entryForm.compositeValidators[type],
      err,
      objValidation,
      controlIsDirty,
      fieldErrorMsg = $(this).attr('data-ngx-control-error-msg') || '',
      requiredErrorMsg = $(this).attr('data-ngx-control-required-msg') || $(this).attr('data-ngx-control-error-msg'),
      isSecondary = form.id === 'xSecondaryForm';

    // if there is a matching validation...
    if ('undefined' !== typeof obj) {
      err = $form.find($el.attr('data-parsley-error-container'));

      controlIsDirty = function($fields) {
        var isDirty = false;
        _.each($fields, function(el) {
          if ($(el).val()) isDirty = true;
        });
        return isDirty;
      };

      // validation handler
      objValidation = function(arg) {
        var force = 'string' === typeof arg && arg === 'force',
          // Fields should only be validated if they are:
          // 1. Visible (returning users fields are hidden if they'd previously provided the field entries), and
          // 2. Required, or
          // 3. Not required and dirty (i.e. user has some kind of input value)
          //
          // <select /> has now been included as WYN-3811 needed to add aria-{describedby,invalid} attributes to
          // all input fields in a composite module (think Address, DOB, etc... with a dropdown). This must be
          // excluded from dirty checking as these selected fields have a non-empty value.
          $fields = $('input:not([type="hidden"],:hidden), select:not(:hidden)', $el),
          $fieldsDirty = $('input:not([type="hidden"],:hidden)', $el),
          allFieldsTouched = true,
          isRequired = $el.hasClass('xRequired');

        if ($fields.length && (isRequired || controlIsDirty($fieldsDirty))) {
          // Only do the validation if all three fields have been touched
          _.each($fields, function(el, idx) {
            if (!el.getAttribute('data-ngx-touched')) allFieldsTouched = false;
          });

          if (allFieldsTouched || force) {
            // Returns an object with each subfield's error state
            var errorTypes = obj.fn.call(this, control, isSecondary, form);
            // Boolean for validity of entire field
            var valid = errorTypes.valid;
            if (valid && NGX.entryForm.compositeCustomValidations.hasOwnProperty(control)) {
              valid = NGX.entryForm.compositeCustomValidations[control].call(this, $fields);
            }
            // Create a unique element id and add to the error wrapper
            var id =
              'xErrorId-' +
              Math.random()
                .toString(16)
                .slice(2);
            err[valid ? 'removeAttr' : 'attr']('id', id);

            $el[(valid ? 'remove' : 'add') + 'Class']('xFieldError');

            var customErrorMsg = errorTypes.isRequiredFailed ? requiredErrorMsg : fieldErrorMsg;
            err.text(errorTypes.msg || customErrorMsg || obj.msg || 'Invalid field');

            // Each input field will either add or remove an aria-describedby attribute and
            // set aria-invalid attribute to true or false
            _.each($fields, function(el, idx) {
              var subFieldErrorKey = el.getAttribute('data-ngx-subfield-error-key');
              if (subFieldErrorKey && !errorTypes[subFieldErrorKey]) {
                var currAriaLabel = el.getAttribute('aria-label');
                if (!/Error/.test(currAriaLabel)) {
                  el.setAttribute('aria-label', `${currAriaLabel} Error`);
                }
                el.setAttribute('aria-describedby', id);
                el.setAttribute('aria-invalid', true);
              } else {
                var ariaLabel = el.getAttribute('aria-label');
                if (ariaLabel) {
                  var removedError = ariaLabel.replace(' Error', '');
                  el.setAttribute('aria-label', removedError);
                }
                el.removeAttribute('aria-describedby');
                el.setAttribute('aria-invalid', false);
              }
            });
            return valid;
          }
        } else {
          if (!isRequired && !controlIsDirty($fieldsDirty)) {
            // If it's not required and not dirty, it may have been cleared, so remove the
            // error class in case it was added.
            $el.removeClass('xFieldError');
          }
          return true;
        }
      };

      // bind fn to the change event of any child inputs. if the wrapper has a data-trigger attr use those values
      $el.on($el.attr('data-parsley-trigger') || 'change, focusout', 'input, select', objValidation.bind($el));

      // populate the submission validation array
      NGX.entryForm.fieldGroups.push(objValidation.bind($el));
    }
  });
};

/**
 * Update UI with calculated date for any minimum age fields only once.
 * Prevent infinite validation events onblur, or one .one() for every validation event triggered
 * @param {Dom element} form
 */

var initMinimumAge = function(form) {
  $('.xComposite[data-ngx-minage]', form).each(function() {
    var controlName = $(this).attr('data-ngx-control'),
      yearField = $('#' + controlName + '_year');

    if (yearField.length > 0) {
      $('input,textarea,select', this).on('blur', function() {
        var date = yearField.data('value');
        if (date && date !== '') yearField.val(yearField.data('value'));
      });
    }
  });
};

/**
 * Apply mobile specific hacks and overrides
 */
var applyMobileOverrides = function(form) {
  log.debug('Applying mobile hacks to forms', 6);

  // Add 'multiple' attribute to work around an iOS issue
  NGX.App.Events.on('form:ready', function() {
    $('[type=file]', form).attr('multiple', 'multiple');
  });
};

/**
 * On upload field change, set a Parsley data attribute for validation later
 */
var applyUploadFieldValue = function() {
  $('[type=file]').change(function(e) {
    var filename = null,
      fieldEl = $(this);

    if (fieldEl && fieldEl.attr('type') == 'file') {
      if (!(window.FileReader && window.File)) {
        // Old browser fallback
        filename = fieldEl.get(0).value || null;
      } else {
        var theFile = fieldEl.get(0).files[0];
        filename = theFile ? theFile.name : null;
      }

      fieldEl.attr('data-parsley-value', filename);

      truncateSelectedFilename(fieldEl, filename);

      fieldEl.parsley().validate();
    }
  });
};

/**
 * Render multi optin list with Handlebars
 * @param {Object} config
 * @param {string} formId
 * @returns {boolean|number}
 */
function renderMultiOptinList(config, formId) {
  if (!config || config.length == 0) return false;

  var optM = 0,
    optL = config.length,
    $el,
    templateMIO = NGX.App.api.getTemplate('forms/multiOptins'),
    totalOptins = 0,
    theFormID = formId || 'xCampaignForm',
    // Hide optins that user has previously selected
    hideExistingOptins = function(cfg) {
      const allowCookies = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.FUNCTIONAL);
      if (!allowCookies) {
        // WYN-6333 Privacy consent - If we do not allow functional cookies, then we simply skip this behavior for
        // multi-optins.  We will not store user choices, nor will we check if they were previously answered or hide them.
        return cfg;
      }

      var storageKey = NGX.Util.getStorageKey(cfg),
        existingOptinString = NGX.StorageManager.get(storageKey);

      if (existingOptinString.length > 0) {
        try {
          var newOpts = [],
            existingOptins = JSON.parse(existingOptinString);

          _.each(cfg.options, function(el) {
            if (!_.contains(existingOptins, el.value)) newOpts.push(el);
          });

          cfg.options = newOpts;
        } catch (e) {
          log.debug(
            'Progressive MultiOptins: Could not parse localStorage/cookie for existing signups, returning all items',
            1
          );
        }
      }

      return cfg;
    },
    // Hide optins that aren't selected for today (by the admin)
    onlyShowTodaysOptions = function(cfg) {
      // To get the relevant date: (getBrowserOffset + getCampaignOffset)

      var browserOffsetFromUTC = moment().utcOffset() / 60,
        campaignOffsetFromUTC = parseInt(NGX.App.config.timezoneOffset) || 0,
        totalOffsetHours = browserOffsetFromUTC - campaignOffsetFromUTC;

      var today = moment()
        .add(totalOffsetHours, 'hours')
        .format('dddd')
        .toLowerCase();

      log.debug('Progressive MultiOptins: The current day at the campaign\'s timezone is: ' + today + '.', 5);

      try {
        var newOpts = [];
        _.each(cfg.options, function(el) {
          if (el.daysShown.indexOf(today) > -1) newOpts.push(el);
        });

        cfg.options = newOpts;
      } catch (e) {
        log.debug('Progressive MultiOptins: Could not filter out options from non-today days.', 1);
      }
      return cfg;
    },
    skipOptinFilter = function(cfg) {
      if (!document.getElementById('xReturningUserForm')) return false; // Not a remember me campaign, don't skip the filtering
      if (!cfg.skipDaysFilter) return false; // Skip is disabled, don't skip at all

      const allowCookies = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.FUNCTIONAL);
      if (!allowCookies) {
        // WYN-6333 Privacy consent - If we do not allow functional cookies, then we simply skip this behavior for
        // multi-optins. We will not store user choices, nor will we check if they were previously answered or hide them.
        return false;
      }
      var storageKey = NGX.Util.getStorageKey(cfg),
        storageVal = NGX.StorageManager.get(storageKey);

      // Skip is enabled, do skip if we find a stored localStorage value OR if user is remembered from before
      return storageVal || NGX.App.state.currentUser.remembered ? false : true;
    };

  for (; optM < optL; optM++) {
    var currConf = config[optM],
      _skipOptinFilter = skipOptinFilter(currConf);

    // Filter out any optins not selected for today
    if (currConf.daysFilter && !_skipOptinFilter) {
      currConf = onlyShowTodaysOptions(currConf);
    }

    // Filter out any optins a user has previously selected
    if (currConf.userFilter && !_skipOptinFilter) {
      currConf = hideExistingOptins(currConf);
    }

    if (currConf.options.length > 0) {
      var id;
      // Render the optins then apply Uniform
      // TODO: Two duplicate lines can be moved out of conditions?
      if (theFormID === 'xCampaignForm' && !currConf.isSecondary) {
        id = '#' + currConf.controlId + 'Wrapper';
        $el = $(id, '#' + theFormID);
        $el.html(templateMIO(currConf));

        initialiseFormStyling($el);
        bindAPItriggers($('#' + theFormID), id);
      } else if (theFormID === 'xSecondaryForm' && $('.xSecondaryForm').length > 0 && currConf.isSecondary) {
        id = '#Secondary_' + currConf.controlId + 'Wrapper';
        $el = $(id);
        $el.html(templateMIO(currConf));

        initialiseFormStyling($el);
        bindAPItriggers($('#' + theFormID), id);
        totalOptins += currConf.options.length;
      }
    }

    currConf.rendered = true;
  }

  // Check or uncheck the multi optins all at once
  $('form').on('change', '[data-ngx-checkall]', function(e) {
    var $el = $(e.currentTarget),
      $parent = $el.parents('.xFieldContainer'),
      targ = $el.attr('data-ngx-checkall'),
      checked = e.currentTarget.checked ? 'checked' : false,
      boxes = $('[name=' + targ + ']', $parent);

    for (var i = 0, n = boxes.length; i < n; i++) {
      boxes[i].checked = checked;
    }
    // Re-apply uniform
    $.uniform.update();
  });

  return totalOptins;
}

// Very rough attempt at truncating the long filenames to approx fit
// the available element size.
function truncateSelectedFilename(fileInput, filename) {
  if (!filename) return;

  // The parent field container element.
  const parentEl = $(fileInput).closest('.xFieldContainer')[0];

  const filenameEl = $(parentEl).find('.xFilename')[0];
  filenameEl.innerText = '';
  const filenameWidth = filenameEl.offsetWidth;
  const truncLength = Math.floor(filenameWidth / 14);
  const ellipsis = filename.length > truncLength ? '...' : '';
  filenameEl.innerText = filename.substring(0, truncLength) + ellipsis;
}

function getRememberMeEmail() {
  var email = null;
  var rememberEmailField = document.getElementById('xReturningUserEmail');
  if (rememberEmailField) {
    email = rememberEmailField.value;
  }
  return email;
}

function rememberMeEnabled() {
  var enabled = document.getElementById('xReturningUserEmail');
  return enabled;
}

/** Get the error message from the helios response (is exists). Returns undefined otherwise.
 * @param response Response object
*/
function getErrorMessage(response) {
  let msg, code;
  if (response && response.responseJSON) {
    const _result = response.responseJSON;
    code = _result.code;
    msg = _result.message || NGX.lang.get(_result.code);
  }
  return [msg, code];
}

/**
 * Handle the situation when the promotion period is closed and user sent a request to helios
 * @param {*} code Error code received from helios
 * @param {*} callback What should we do if it is not the error of expired promotion period
 */
function handleClosedPromoPeriod(code, callback = () => {} ) {
  if (code === enums.SUBMIT_RESPONSE_ERROR_CODES.PROMOTION_CLOSED) {
    if (NGX.App.state.shownPromo) {
      NGX.App.api.action('promo:close:'+NGX.App.state.shownPromo, {action: 'promo:close:'+NGX.App.state.shownPromo});
      window.location.reload();
    }
  } else callback();
}

/**
 * Function replaces a form with the Paragraph with provided text
 * See WYN-7646
 * @param {*} text Text of the paragraph
 * @param {*} form The form element which should be replaced
 * @returns
 */
function replaceForm(text, form) {
  if (!form) return;
  const el = document.createElement('div');
  el.innerHTML = text;
  el.classList.add('xCopy', 'xEntryClosedMessage');
  el.id = `xFormEntryClosed`;
  form.replaceWith(el);
}

export default (function() {
  var _form,
    _secondaryForm,
    _returningUserForm,
    html = document.documentElement || document.getElementsByTagName('html')[0],
    customSubmitHandlers = [];

  var init = function(moduleData) {
    log.debug('Initialising Form', 2);

    const _nForms = document.querySelectorAll('#xCampaignForm');
    _form = _nForms[0];

    _secondaryForm = document.getElementById('xSecondaryForm');
    _returningUserForm = document.getElementById('xReturningUserForm');

    NGX.App.config.hasSecondaryForm = _secondaryForm ? true : false;

    this.el = _form;
    this.$el = $(_form);
    this.validators = ngxValidators;
    this.compositeValidators = compositeValidators;
    this.customValidations = {};
    this.compositeCustomValidations = {};
    this.fieldGroups = [];
    this.validations = {};
    this.basicMode = moduleData.formBasicMode === 'on';

    let shouldHideForms = false;
    if (!!NGX.App.config.entryBounds && !!NGX.App.config.entryBounds.entryTimeFrom) {
      const currentTime = getCurrentCampaignDateTime(NGX.App.config.timezone);
      shouldHideForms = !isTimeBetween(
        currentTime,
        NGX.App.config.entryBounds.entryTimeFrom,
        NGX.App.config.entryBounds.entryTimeTo
      );
    }

    if (shouldHideForms) {
      replaceForm(NGX.App.config.entryBounds.entryTimeCloseMessage, _secondaryForm);
      replaceForm(NGX.App.config.entryBounds.entryTimeCloseMessage, _returningUserForm);
    }

    _nForms.forEach( (nForm, idx) => {
      if (shouldHideForms) {
        replaceForm(NGX.App.config.entryBounds.entryTimeCloseMessage, nForm);
      }

      nForm.dataset.orderNum = idx;
      if (Browser.isMobile()) {
        applyMobileOverrides(nForm);
      } else if (moduleData && !this.basicMode) {
        initialiseSelectBoxes(nForm);
      }
      // If remember me is not enabled, initialise now, otherwise leave initialisation
      // until after the remember me xhr callback has occured.
      if (!rememberMeEnabled()) {
        initialiseProgressiveFields(nForm);
      }

      initialiseValidation(nForm);
    });

    if (Browser.supportsUpload()) {
      // Force data-parsley-value on all file upload changes
      NGX.App.Events.on('form:ready', applyUploadFieldValue);
      NGX.App.Events.on('form:ready', videoUpload.init);
    } else {
      // Disable upload fields for unsupported browsers
      NGX.App.Events.once('form:ready', formUtil.disableUpload);
    }

    // Ignore <Enter> keypress
    $('input,select', _form).keypress(function(event) {
      return event.keyCode !== 13;
    });

    // Mark elements within a composite group as `touched` - used later for validation
    $('.xComposite').on('focus', 'input,select', function(e) {
      $(this).attr('data-ngx-touched', 'true');
    });

    // Trigger a resize as soon as we trigger the entry route
    NGX.App.Events.on('action:route:entry', function() {
      setTimeout(formUtil.formWrapperResize, 500);
    });

    // Generate Insight Events using out form events. This is a bit of a cheat, and we
    // might want to look at moving this to the Insights.js module.
    //
    // UNCOMMENT TO ENABLE
    //
    //NGX.Analytics.once(PUB.FORM_MODULE_INTERACTION, function (params) {
    //  NGX.Analytics.trigger(PUB.INSIGHT_FIRSTINTERACTION, params);
    //});

    NGX.App.Events.trigger('form:ready');
  };

  var renderMultiOptIns = function(config, formId) {
    return renderMultiOptinList(config, formId);
  };

  /**
   * Present a Google Recaptcha and require its successful completion before calling the callback
   * @param {Function} callback
   * @param {string} formIdToSubmit ID of the form which we are going to submit
   * @param {HTMLElement} formElem Reference to the form DOM node which we are going to submit
   * @param {boolean} smartCaptchaRequired If the server required the captcha and we must show the captcha window
   */
  var validateCaptcha = function(callback, formIdToSubmit, formElem, smartCaptchaRequired = false) {
    const $form = $(formElem || formIdToSubmit ? formIdToSubmit : _form);

    const campaignFraudDetectionStrategy = NGX.App.config.fraudDetectionStrategy || enums.STRATEGY_FRAUD_NONE;
    const isNoneFraudStrategy = campaignFraudDetectionStrategy === enums.STRATEGY_FRAUD_NONE;

    const isDynCaptchaStrategy = [
      enums.STRATEGY_FRAUD_DYNCAPTCHA,
      enums.STRATEGY_FRAUD_DYNCAPTCHA_TURNSTILE
    ].includes(campaignFraudDetectionStrategy);

    // If captcha disabled, just call the callback
    // Or, it is the Smart Captcha and we firstly need to make an attempt of submission
    if (isNoneFraudStrategy || (isDynCaptchaStrategy && !smartCaptchaRequired) ) {
      callback.call(this, $form[0]);
    } else {
      if ([enums.STRATEGY_FRAUD_CAPTCHA, enums.STRATEGY_FRAUD_TURNSTILE].includes(campaignFraudDetectionStrategy) ||
        (isDynCaptchaStrategy && smartCaptchaRequired)
      ) { // Google reCaptcha is used for the fraud detection
        const captchaParams = formUtil.getCaptchaParams(campaignFraudDetectionStrategy);
        const resetCaptcha = () => {
          window[captchaParams.libName][captchaParams.resetMethod]();
        }
        verifyHuman(function(rsp) {
          var failureDialog = {
            template: NGX.App.api.getTemplate('inviteModal'),
            content:  {
              title:        NGX.lang.get('form.captcha.failed.title'),
              msg:          NGX.lang.get('form.captcha.failed.msg'),
              closeBtnText: NGX.lang.get('buttons.overlayClose'),
            },
            onclose: resetCaptcha,
          };
          // On success, add the token to the form for later backend checks
          if (rsp) {
            NGX.App.api.setFormField('ngx_captcha_token', rsp, 'hidden', formIdToSubmit, formElem);
            NGX.App.Events.trigger('form:submit');
            // Call the callback
            callback.call(this, $form[0]);
            // Reset the captcha library. We need to do this because otherwise we will use the same token in the
            // rememberMe form, and in the main campaign form. If we will use the same token, then Helios backend will
            // retyrb 503 error (we need to use new token each request)
            resetCaptcha();
          } else {
            // Show the error dialog
            $form.find('.xSubmit').attr('disabled', null);
            Overlay.create(failureDialog);
          }
        }, captchaParams.libName, captchaParams.sdk, true);
      }
    }
  };



  /**
   * Submit a form
   * @param {Dom element} formEl
   * @param {Function} callback
   */

  var submitCampaignObjectForm = function(formEl, callback, novalidate = false) {

    formEl = formEl || _form;
    var user = NGX.App.state.currentUser.userSession,
      _frmMethod = 'post',
      // Create submission handler for when FB permissions are required to enter
      doFbLogin = function() {
        NGX.Util.fbLogin(
          function(response) {
            // has tried to login
            if (response && response.authResponse) {
              NGX.App.state.currentUser.userSession = response;
              // attempt to auth
              FB.api(
                '/' + response.authResponse.userID + '?access_token=' + response.authResponse.accessToken,
                function(response) {
                  if (response && !response.error) {
                    // Populate the form's hidden FB data fields
                    NGX.Form.populateFormData(formEl, response, true);
                  }
                }
              );
            } else if (user && user.status === 'connected') {
              // already logged in and authed
              // Populate the form's hidden FB data fields
              NGX.Form.populateFormData(formEl, response, true);
            } else if (user && user.status === 'not_authorized') {
              // User cancelled
              // Re-enable submit button
              $('.xSubmit', formEl).attr('disabled', null);
              // Show error message if user hasn't granted permissions
              Overlay.create({
                type:     'modal',
                template: NGX.App.api.getTemplate('inviteModal'),
                content:  {
                  msg:          NGX.lang.get('permissions.error.message'),
                  title:        NGX.lang.get('permissions.error.title'),
                  closeBtnText: NGX.lang.get('buttons.overlayClose'),
                },
              });
            }
          },
          { timing: 'submit' }
        );
      },

      // Check submission strategy and fire the callback
      submissionStrategy = function(callback) {
        // Send via validateCaptcha - will pass straight through if captcha isn't enabled
        validateCaptcha(function submission() {
          // if invite on submit is configured do that and pass the fbLogin as a callback

          $(formEl).addClass('isSubmitting');

          // Add the TTL token, contains a timestamp and a verification code exclusive for the campaign
          if (NGX.info.timestampToken) {
            NGX.App.api.setFormField('ngx_t_token2', NGX.info.timestampToken, 'hidden', formEl.id, formEl);
          }

          // Adding info about the user interaction with the page

          NGX.TrackingService.getTrackingTokens(formEl, null, NGX.App.config.shouldAddTrackingTokens).then( res => {
            if (NGX.App.config.shouldAddTrackingTokens) {
              // Now the data is encrypted and can be sent.
              NGX.App.api.setFormField('ngx_e_token1', res.token1, 'hidden', formEl.id, formEl);
              NGX.App.api.setFormField('ngx_e_token2', res.token2, 'hidden', formEl.id, formEl);
            }
            // If this is a scale fast entry (Helios) and the browser supports FormData
            if ($(formEl).attr('data-ngx-method') === 'ajax' && 'FormData' in window) {
              var _sendData = {},
                _checkboxes = {};

              var _frmData = new FormData();
              // Add isXHR flag
              _frmData.append('isXHR', true);

              // Populate form data with checkboxes (except Check All).
              var _checkboxFields = {};
              $('.checkField:checked:not([data-ngx-checkall])', formEl).each(function(idx, chk) {
                var _chkName = chk.name.replace('[]', ''),
                  $chk = $(chk);

                // Keep track of checkbox values separate to FormData object so that we can diff against it
                // for unchecked checkbox fields further down.
                if (!_checkboxFields[_chkName]) {
                  // Store as Array to support multiple values per field name.
                  _checkboxFields[_chkName] = [];
                }
                _checkboxFields[_chkName].push(chk.value);

                const dataAttr = $chk.attr('data-ngx-progoptin');
                // WYN-6333 Privacy consent - If we do not allow functional cookies, then we will not save selected optin answers.
                const allowCookies = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.FUNCTIONAL);
                if (dataAttr && allowCookies) {
                  // Store selected opt ins in local storage/cookie
                  var _filterScope = $chk.attr('data-ngx-filterscope') || 'account',
                    _skey = NGX.Util.getStorageKey({ controlName: _chkName, filterScope: _filterScope });
                  NGX.StorageManager.push(_skey, dataAttr);
                }
              });

              // WYN-4576 NOTE - We now also submit checkbox fields that have not been checked(!) to mirror MX functionality...
              $('.checkField:not(:checked, [data-ngx-checkall])', formEl).each(function(idx, chk) {
                var _chkName = chk.name.replace('[]', ''),
                  $chk = $(chk);

                // If not already a checked checkbox field with a value, then add it with blank value for submission with form.
                if (!_checkboxFields[_chkName]) {
                  _checkboxFields[_chkName] = [''];
                }
              });

              // Add combined list of checkbox fields to FormData object.
              for (var checkboxField in _checkboxFields) {
                for (var checkboxFieldValue in _checkboxFields[checkboxField]) {
                  _frmData.append(checkboxField, _checkboxFields[checkboxField][checkboxFieldValue]);
                }
              }

              // Add standard inputs
              var _formData = $(':input:not(.checkField)', formEl).serializeArray();
              _.each(_formData, function(i) {
                _frmData.append(i.name, i.value);
              });

              // Add file inputs (exlude direct video uploads, these are uploaded separately and referenced by a token instead).
              $.each($('[type="file"]:not([data-ngx-type="video"])', formEl), function(i, el) {
                if (el.files && el.files.length > 0) {
                  $.each(el.files, function(f, file) {
                    _frmData.append(el.name, file);
                  });
                }
              });

              processProgressiveFields(formEl, getRememberMeEmail());

              // Add callback host param.
              _frmData.append('cbh', getCallbackHost());


              // Privacy management gating on user cookie consent. We will send a flag to the BE to allow or disallow
              // it returning a set-cookie param in the header for the ngx cookie (analytics).
              const allowCookies = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.ANALYTICS);
              _frmData.append('ngx_send_cookies', allowCookies);

              // DEPRECATED: Send cross domain data via _GET for old IE
              if (_frmData.fake) {
                _frmData = _frmData.serialize();
                _frmMethod = 'GET';
              }

              // Send the request to Helios
              $.ajax({
                type:        _frmMethod,
                url:         formEl.action,
                contentType: false,
                processData: false,
                data:        _frmData,
                dataType:    'json',
                xhrFields:   {
                  // Include the cookie
                  withCredentials: true,
                },
              })
                .success(function(response) {
                  if (response.state === enums.FORM_SUMBISSION_RESP_STATUS.SUCCESS) {
                    // If the response includes a URL, use it for a redirect
                    if (response.url) {
                      var redirectToConfirm = function() {
                        // TODO [SG]: Temp jQuery here

                        if (NGX.App.config.singlePageCampaign) {
                          // In the case of SPA, don't redirect, just trigger the confirmation state and add some classes
                          $('body')
                            .removeClass('notEntered')
                            .addClass('hasEntered');
                          _.extend(NGX.App.state, { entry: response.entry });
                          NGX.App.api.state('confirmation');
                          NGX.App.Events.trigger('entry:success:spa');
                        } else {
                          // Redirect to the response URL, including the entry as params
                          window.location = getRedirectURL(response.url, response.entry);
                        }
                      };

                      NGX.Analytics.trigger(PUB.FORM_ENTRY_SUCCESS, { payload: response.entry });

                      // If the embed API is in use, give the parent the entry and, if no response with 5s, redirect anyway
                      if (NGX.App.state.useApi) {
                        NGX.App.api.messageParent(
                          { action: 'event', event: 'form:entry:broadcast', payload: response.entry },
                          redirectToConfirm
                        );
                        setTimeout(redirectToConfirm, 5000);
                      } else {
                        // Redirect to the given response url
                        redirectToConfirm();
                      }
                    }
                  } else if (response.state === enums.FORM_SUMBISSION_RESP_STATUS.ALREADY_ENTERED) {
                    // If the user has already entered, show a modal with an error message
                    NGX.Analytics.trigger(PUB.FORM_ENTRY_ALREADYENTERED);
                    Overlay.create({
                      type:     'modal',
                      template: NGX.App.api.getTemplate('inviteModal'),
                      content:  {
                        title:        NGX.lang.get('form.returningUser.entered.title'),
                        msg:          response.message, // NGX-5056
                        closeBtnText: NGX.lang.get('buttons.overlayClose'),
                      },
                    });
                  }
                })
                .fail(function(fail) {
                  const [_message, _code] = getErrorMessage(fail);
                  // Re-enable submit button and remove class
                  $(formEl).removeClass('isSubmitting');
                  $('.xSubmit', formEl).attr('disabled', null);
                  // The request had been failed because of the Smart Captcha
                  if (_code === enums.SUBMIT_RESPONSE_ERROR_CODES.CAPTCHA_REQUIRED) {
                    validateCaptcha(submission, formEl.getAttribute('id'), formEl, true);
                  }
                  // The request had been failed because of other reasons - notify the user
                  else {
                    NGX.Analytics.trigger(PUB.FORM_ENTRY_FAILED);
                    // Show modal with error message
                    Overlay.create({
                      type:     'modal',
                      template: NGX.App.api.getTemplate('inviteModal'),
                      content:  {
                        title:        NGX.lang.get('form.fail.title', 'Error!'),
                        msg:          _message,
                        closeBtnText: NGX.lang.get('buttons.overlayClose'),
                      },
                      onclose: () => {
                        handleClosedPromoPeriod(_code);
                      },
                    });
                  }
                })
                .done(function() {
                  // Remove the submitting class from the form
                  $(formEl).removeClass('isSubmitting');
                });
            } else {
              // Standard (coreapp/grails) entry mechanism

              $('.checkField:checked:not([data-ngx-checkall])', formEl).each(function(idx, chk) {
                var _chkName = chk.name.replace('[]', ''),
                  $chk = $(chk);
                const dataAttr = $chk.attr('data-ngx-progoptin');
                // WYN-6333 Privacy consent - If we do not allow functional cookies, then we will not save selected optin answers.
                const allowCookies = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.FUNCTIONAL);
                if (dataAttr && allowCookies) {
                  var _filterScope = $chk.attr('data-ngx-filterscope') || 'account',
                    _skey = NGX.Util.getStorageKey({ controlName: _chkName, filterScope: _filterScope });
                  NGX.StorageManager.push(_skey, dataAttr);
                }
              });

              processProgressiveFields(formEl, getRememberMeEmail());

              if (_cntx.isFB === false || NGX.App.config.permissionRequest.timing === 'never') {
                // Submit directly if no FB required
                formEl.submit();
              } else {
                // Call the FB based submission mechanism
                callback();
              }
            }
          });
        }, formEl.getAttribute('id'), formEl);
      };

    // if it's a hidden form we don't need to validate
    if ((formEl.id && formEl.id === 'xHiddenForm') || novalidate) {
      submissionStrategy(doFbLogin);
    } else {
      // Check form is valid.
      var formValid = NGX.Form.validate(formEl);
      // Only show the error message for the Submit button if there is an invalid field
      if (!formValid) {
        $('.xSubmitContainer, .xActivateContainer', formEl).addClass('xFieldError xFormValidated');
        $('.xSubmit', formEl).attr('aria-describedby', 'xSubmitButtonError');
      }

      // Check all registered custom handlers are valid.
      var customSubmitHandlersValid = true;
      for (var customSubmitHandler in customSubmitHandlers) {
        var handlerResponse = customSubmitHandlers[customSubmitHandler](formEl);
        if (!handlerResponse) {
          customSubmitHandlersValid = false;
        }
      }

      if (formValid && customSubmitHandlersValid) {
        $('.xSubmit', formEl).removeAttr('aria-describedby');
        $('.xSubmitContainer, .xActivateContainer', formEl).removeClass('xFieldError');
        // API
        if (!$('.xSubmit', formEl).attr('disabled')) {
          NGX.App.Events.trigger('form:submit');
          NGX.Analytics.trigger(PUB.FORM_SUBMIT);
          submissionStrategy(doFbLogin);
          $('.xSubmit', formEl).attr('disabled', 'disabled');
        }
      }
    }
  }; // END submit

  /**
   * For remember me entries, check if the user has entered (by email)
   * @param {HTMLElement} triggeredForm A form instance which is triggering the submit
   * @returns {boolean}
   */
  var checkReturningUser = function(triggeredForm) {
    var mainCampaignForm = _form,
      secondaryForm = _secondaryForm,
      returningUserForm = triggeredForm || document.getElementById('xReturningUserForm'),
      $returningUserEmail = $('#xReturningUserEmail', triggeredForm),
      _emailAddr = $returningUserEmail.val(),
      _sendData = {},
      _totalSecondary = $('input:not([type=hidden]),textarea,select', secondaryForm).size();
    var showMainForm = function(formID) {
      var theFormID = formID || 'xCampaignForm',
        $theForm = $('#' + theFormID, $(returningUserForm).parent());
      if (NGX.uiFlow.get(formID)) {
        NGX.uiFlow.get(formID).model.set('viewed', false);
      }
      $('[type="email"]', $theForm)
        .eq(0)
        .val(_emailAddr);
      // Hide the returning user form
      $(returningUserForm).addClass('xHidden');
      // Hide the main entry forms
      $('#xCampaignForm,#xSecondaryForm').addClass('xHidden');

      // Unhide the selected form (either primary or secondary)
      $theForm
        .removeClass('xHidden')
        .parents('.xHidden')
        .removeClass('xHidden');

      // Re-apply uniform
      $.uniform.update();

      if (NGX.uiFlow.get(theFormID)) {
        NGX.uiFlow.get(theFormID).model.set('viewed', true);
      }

      initialiseSelectBoxes($theForm);

      initialiseProgressiveFields($theForm, getRememberMeEmail());

      // Don't re-initialize validation on the main form as it will have already been done once during the form init logic
      // (eg. if after checking user if they are returned as 'unknown_user', showMainForm will be called targeting the main form again, rather than secondary).
      if (theFormID != 'xCampaignForm') {
        initialiseValidation($theForm[0]);
      }

      // If only one form page, unhide the enter button
      if ($('.xFormPage', $theForm).length === 1) {
        $('.xActivateContainer', $theForm).removeClass('xHidden');
      } else {
        // unhide the next button
        $('.xActionNext').removeClass('xDisabled');
      }

      // Resize the main form, and the iframe
      formUtil.formWrapperResize();
      NGX.App.api.resizeParent();
    };

    if (!formUtil.validateEmail(_emailAddr)) {
      // Show error message if submitted email is invalid
      Overlay.create({
        type:     'modal',
        template: NGX.App.api.getTemplate('inviteModal'),
        content:  {
          title:        NGX.lang.get('form.returningUser.invalid.title'),
          msg:          NGX.lang.get('form.returningUser.invalid.msg'),
          closeBtnText: NGX.lang.get('buttons.overlayClose'),
        },
      });
      return false;
    }

    // Check if user is old enough to see the campaign, before sending the request
    // Privacy management note: coppa cookie usage is "strictly necessary" and doesn't need to be privacy-gated.
    // If cookies are browser-disabled, we display a blocking cookies-disabled modal and the experience & won't get here.
    var _blocked = NGX.StorageManager.get('gate_' + NGX.App.config.tenant),
      _hashed = NGX.Util.hashCode(_emailAddr);

    if (_blocked.indexOf(_hashed) > -1) {
      // Gate the campaign
      NGX.App.api.gate({ type: 'Age' });
    } else {
      _sendData = {
        email:            $returningUserEmail.val(),
        apikey:           _cntx.apikey,
        campaignObjectId: NGX.App.config.id,
        ngx_remember_me:  true,
        ngx_check_entry:  NGX.App.config.hasSecondaryForm,
        isXHR:            true,
      };

      // WYN-6447 Adding data about promotionId, channel, etc. (everything from the hidden inputs in the campaign form)
      var _formData = $('.xFormPages > input', mainCampaignForm).serializeArray();
      _.each(_formData, function(i) {
        _sendData[i.name] = i.value;
      });

      if (NGX.Browser.queryStringObject('stageMode') == 'true') {
        _sendData.stageMode = true;

        if (_cntx.stageToken) {
          _sendData.ptk = _cntx.stageToken;
        }
      }

      // Add callback host param.
      _sendData.cbh = getCallbackHost();

      // Validate captcha if required (WYN-6590)
      validateCaptcha( function submission() {
        const captchaToken = document.getElementById('xReturningUserForm_ngx_captcha_token'),
          _captchaToken = captchaToken ? captchaToken.value : null;
        if (_captchaToken) {
          _sendData.ngx_captcha_token = _captchaToken;
        }

        // Add the TTL token, contains a timestamp and a verification code exclusive for the campaign
        if (NGX.info.timestampToken) {
          _sendData.ngx_t_token2 = NGX.info.timestampToken;
        }

        // Adding info about the user interaction with the page
        NGX.TrackingService.getTrackingTokens(null, _sendData, NGX.App.config.shouldAddTrackingTokens).then( res => {
          if (NGX.App.config.shouldAddTrackingTokens) {
            // Now the data is encrypted and can be sent.
            _sendData.ngx_e_token1 = res.token1;
            _sendData.ngx_e_token2 = res.token2;
          }
          // Make request to Helios to see if user is known
          $.ajax({
            url:       returningUserForm.action,
            method:    returningUserForm.method,
            dataType:  'json',
            data:      _sendData,
            xhrFields: {
              withCredentials: true,
            },
          })
            .success(function(response) {
              let _multiOptIns = window.ngxMultiOptIns || [],
                _redirectUrl;

              if (response.state === enums.FORM_SUMBISSION_RESP_STATUS.UNKNOWN_USER) {
                NGX.App.Events.trigger('form:returninguser:unknownuser');
                if (typeof ngxMultiOptIns !== 'undefined') {
                  _totalSecondary += NGX.entryForm.renderMultiOptIns(ngxMultiOptIns, 'xCampaignForm') || 0;
                }
                // Show main form to unknown users
                showMainForm();
              } else if (response.state === enums.FORM_SUMBISSION_RESP_STATUS.SUCCESS) {
                NGX.App.Events.trigger('form:returninguser:remembered');
                // Redirect to response URL for remembered users
                if (response.url) {
                  _redirectUrl = getRedirectURL(response.url, response.entry);
                  window.location = _redirectUrl;
                }
              } else if (response.state === enums.FORM_SUMBISSION_RESP_STATUS.KNOWN_USER) {
                // Show secondary form to remembered users
                NGX.App.Events.trigger('form:returninguser:remembered');
                NGX.App.state.currentUser.remembered = true;
                _totalSecondary += NGX.entryForm.renderMultiOptIns(_multiOptIns, 'xSecondaryForm') || 0;

                // Always set the email on the secondary form.
                NGX.App.api.setFormField('email', response.key.substring(6), 'hidden', 'xSecondaryForm');

                NGX.App.api.setFormField('ngx_remember_me', 'true', 'hidden', 'xSecondaryForm');

                if (_totalSecondary > 0) {
                  NGX.App.Events.trigger('form:secondary:show');
                  showMainForm('xSecondaryForm');
                } else {
                  // NGX-5181 Becuase showMainForm is not called (and thus initialiseValidation is also not called), the custom submit
                  // handler is not applied to the secondary form, and as such, the new ajax submit functionality is not executing when it needs to.
                  // Instead, directly call the submitCampaignObjectForm() method, bypassing validation.
                  NGX.entryForm.submitCampaignObjectForm($('#xSecondaryForm')[0], null, true);
                }
              } else if (response.state === enums.FORM_SUMBISSION_RESP_STATUS.ALREADY_ENTERED) {
                // User has entered the max number of times, show modal error message
                NGX.App.Events.trigger('form:returninguser:alreadyentered');
                Overlay.create({
                  type:     'modal',
                  template: NGX.App.api.getTemplate('inviteModal'),
                  content:  {
                    title:        NGX.lang.get('form.returningUser.entered.title'),
                    msg:          response.message, // NGX-5056
                    closeBtnText: NGX.lang.get('buttons.overlayClose'),
                  },
                });
              }
            })
            .fail(function(response) {
              const [_message, _code] = getErrorMessage(response);
              // The request had been failed because of the Smart Captcha
              if (_code === enums.SUBMIT_RESPONSE_ERROR_CODES.CAPTCHA_REQUIRED) {
                validateCaptcha(submission, formEl.getAttribute('id'), formEl, true);
              }
              // The request had been failed because of other reasons
              else {
                // Show modal error message on submission failure (network error etc)
                Overlay.create({
                  type:     'modal',
                  template: NGX.App.api.getTemplate('inviteModal'),
                  content:  {
                    title:        NGX.lang.get('form.returningUser.failed.title'),
                    msg:          _message || NGX.lang.get('form.returningUser.failed.msg'),
                    content:      '<a class="close">X</a>',
                    closeBtnText: NGX.lang.get('buttons.overlayClose'),
                  },
                  onclose: () => {
                    handleClosedPromoPeriod(_code, showMainForm);
                  },
                });
              }
            });
        });
      }, 'xReturningUserForm', returningUserForm);
    }
  };

  /**
   * WYN-4109 Processes any progressive fields during form submit.
   */
  var processProgressiveFields = function(form, email) {
    const allowCookies = NGX.PrivacyManager.doAllowActionType(enums.PRIVACY_TYPES.FUNCTIONAL);
    if (!allowCookies) {
      // WYN-6333 Privacy consent - If we do not allow functional cookies, then we simply skip this behavior for progressive
      // fields.  We will not store user choices, nor will we check if they were previously answered or hide them.
      return;
    }

    // Retrieve progressive store from localstorage and parse to native object.
    var store = NGX.StorageManager.get(PROGRESSIVE_FILTERING_KEY) || '{}';
    store = JSON.parse(store);

    // The store root object.
    var storeRoot = null;

    // The campaigns id.
    var campaignId = NGX.App.config.id;

    // Resolve the stores primary key.
    if (email) {
      // If remember me is enabled and a user email is supplied, this
      // will be a hash of their email.
      var emailHash = hashCode(email);
      if (!store[emailHash]) {
        store[emailHash] = {};
      }
      storeRoot = store[emailHash];
      // Otherwise it will be store root.
    } else {
      storeRoot = store;
    }

    // Initialize the campaign key if required.
    if (!storeRoot[campaignId]) {
      storeRoot[campaignId] = {};
    }

    // Find all progressive fields (exluding previously answered fields).
    $(form)
      .find('.xFieldItem[data-ngx-progressive="true"]:not([data-ngx-progressive-answered="true"])')
      .each(function(index, element) {
        // Get the fields id.
        var id = $(element).attr('data-ngx-id');

        // Hash the id before storing, avoids storing any special characters in cookie which can subsequently trip up the server.
        var idHash = hashCode(id);

        // Work out if the current field has a value or not.
        var fieldValue = null;
        $(element)
          .find('input:checked, select')
          .each(function(fieldIndex, fieldElement) {
            fieldValue = $(fieldElement).val();

            // If it does have a value.
            if (fieldValue) {
              // Store answered boolean against this fields id and 'a' prop.
              if (!storeRoot[campaignId][idHash]) {
                storeRoot[campaignId][idHash] = {
                  a: 1,
                };
              }

              // If we have any value, we can break from the loop immediately.
              return;
            }
          });
      });

    // Serialise and save the updated object back into localstorage.
    store = JSON.stringify(store);
    NGX.StorageManager.set(PROGRESSIVE_FILTERING_KEY, store);
  };

  /* Allow arbitrary handlers to be registered, these will be executed prior to allowing form submission */
  var registerCustomSubmitHandler = function(key, func) {
    customSubmitHandlers[key] = func;
  };

  var getCallbackHost = function() {
    // Get display url (includes protocol+host+port) from context.
    let cbh = NGX.context.displayUrl;

    // Are we in stage/preview?
    if (NGX.context.isStage) {
      cbh = cbh.replace('display', 'app');
    }

    return cbh;
  };

  // Closure to contain all video upload functionality.
  var videoUpload = (function() {
    var config = {
      // Upload chunk size. 5Mb.
      chunkSize:           5120,
      // Exponential backoff delay.
      backoffDelay:        2000,
      // Classname to use as a prefix before all progress bar elements.
      progressClassPrefix: 'xUploadProgress',
      // Chunk size in bytes for AWS API
      awsChunkSize:        1024 * 1024 * 5
    };

    var state = {
      // The current url provided by Helios.
      uploadUrl:        null,
      // The current token provided by Helios.
      uploadToken:      null,
      // Boolean indicating if the upload url has expired yet.
      uploadUrlExpired: true,
      //  The current exponential backoff index.
      backoffIndex:     1,
      // Boolean indicating if we have an error label container by default.
      noDefaultError:   false,
    };

    /**
     * Init video upload functionality.
     */
    var init = function() {
      // Get all video upload inputs.
      const videoInputs = $('[type=file][data-ngx-type=video]');

      // If there are any video upload inputs.
      if (videoInputs.length > 0) {

        const inputId = videoInputs[0].id;
        for (const videoInput of videoInputs) {
          // Get handle on the first one (only a single upload field is allowed per campaign).
          if (inputId !== videoInput.id) continue;
          // One particular case when we can have 2 inputs is the reveal promotion campaign with "Content" page.
          // Because of implementation (2 forms with same IDs, etc.), this ugly hack was introduced.
          let existingTypes = $(videoInput).attr('accept');

          // Modify the accept attribute on MS Edge to remove mkv support.
          if (navigator.msSaveBlob) {
            existingTypes = existingTypes.replace(',.mkv', '');
          }

          // Modify the accept attribute on mobile/ios to force only video types.
          $(videoInput).attr('accept', existingTypes);
          $(videoInput).attr('data-ngx-accept', existingTypes);
          if (Browser.isMobile()) {
            $(videoInput).attr('accept', 'video/*');
          }

          // Setup listener for file change.
          $(videoInput).change(() => {
            handleInputChange(videoInput);
          });
        }
      }
    };

    /**
     * Handles change event for video upload file input field.
     */
    var handleInputChange = function(videoInput) {
      // Read file info.
      readFile(videoInput);

      // Do any initial setup for elements used by the video uploader.
      const [helpContainer, helpLabel, errorLabel] = initElements(videoInput);

      // Fail if the file is not valid.
      if (
        !$(videoInput)
          .parsley()
          .isValid()
      ) {
        // Remove progress bar.
        destroyProgress(videoInput);

        // If validation failed, reset the state of the help/error/progress messages.
        resetMessages(helpLabel, helpContainer, errorLabel);

        return false;
      }

      // Always expire existing url if file input has changed.
      state.uploadUrlExpired = true;

      // Get upload url from Helios.
      // We have 2 params in promise callback here:
      // - uploadUrl - this is a single URL, will be received if we work with old (Google) storage
      // - uploadUrls - this is an array of URLs, will be received if we work with AWS
      // In case of AWS, we need manually split the file to chunks and upload them
      // to URLs from uploadUrls array.
      getUploadUrl(state.uploadFileType).then( ([uploadUrl, uploadUrls]) => {
        // Disable form submit button whilst we are uploading.
        disableSubmit();

        // Render progress bar.
        const [progressBarContainerEl, progressBarEl, progressTextEl] = renderProgress(videoInput);

        // Upload the video.
        uploadVideo(
          videoInput,
          uploadUrl,
          state.uploadFile,
          progressBarEl,
          progressTextEl,
          helpLabel,
          helpContainer,
          errorLabel,
          uploadUrls
        );
      });
    };

    /**
     * Get info about the selected video file.
     */
    var readFile = function(videoInput) {
      // Get handle on the file.
      state.uploadFile = videoInput.files[0];

      // If we have a file.
      if (state.uploadFile) {
        // Attempt to read its filename.
        state.uploadFilename = state.uploadFile.name;

        // Attempt to read its mime type.
        let fileType = state.uploadFile.type;

        // If we could not automatically read it, then fallback to this simple mapping.
        if (!fileType) {
          const typeMapping = {
            mp4:  'video/mp4',
            m4v:  'video/mp4',
            webm: 'video/webm',
            mkv:  'video/x-matroska',
            mov:  'video/quicktime',
          };

          const extension = state.uploadFilename.split('.').pop();

          if (typeMapping[extension]) {
            fileType = typeMapping[extension];
          }
        }
        state.uploadFileType = fileType;
        // Saving file size in bytes
        state.fileSize = state.uploadFile.size;
      }
    };

    var initElements = function(videoInput) {
      // The parent field container element.
      const parentEl = $(videoInput).closest('.xFieldContainer')[0];
      const helpContainer = $(parentEl).find('.xHelpContainer')[0];

      // Truncate visible filename.
      truncateSelectedFilename(videoInput, state.uploadFilename);

      // Create error label element if required.
      var errorLabel = $(helpContainer).find('.xErrorLabel')[0];
      if (!errorLabel) {
        const errorLabelEl = document.createElement('div');
        errorLabelEl.classList.add('xErrorLabel');
        helpContainer.appendChild(errorLabelEl);
        errorLabel = errorLabelEl;
        state.noDefaultError = true;
      }

      // Create help label element if required.
      var helpLabel = $(helpContainer).find('.xHelpLabel')[0];
      if (!helpLabel) {
        const helpLabelEl = document.createElement('div');
        helpLabelEl.classList.add('xHelpLabel');
        helpContainer.insertBefore(helpLabelEl, errorLabel);
        helpLabel = helpLabelEl;
      }

      // Backup initial help message if defined incase we need to restore it.
      if (typeof state.initialHelp === 'undefined') {
        state.initialHelp = helpLabel.innerText;
      }

      return [helpContainer, helpLabel, errorLabel];
    };

    /**
     * Return a video upload url.
     */
    var getUploadUrl = function(uploadFileType) {
      return new Promise((resolve, reject) => {
        // If we need a new endpoint.
        if (state.uploadUrlExpired || state.uploadUrl === null) {
          // Request new endpoint for upload.
          $.ajax({
            type: 'GET',
            url:  NGX.App.config.helios2URLSecure + '/api/interact/d/video/uploadUrl',
            data: {
              apikey:        _cntx.apikey,
              campaignId:    NGX.App.config.id,
              contentType:   uploadFileType,
              origin:        location.origin,
              contentLength: state.fileSize
            },
            dataType: 'json',
          })
            // If we received an endpoint from Helios 2.
            .success(response => {
              // Extract params from response and store.
              state.uploadUrl = response.url;
              state.uploadUrlExpired = false;
              state.uploadToken = response.token;
              // Taking an array of uploadUrls for newly added AWS API
              state.uploadUrls = response.urls;
              state.uploadId = response.uploadId;

              // Resolve promise.
              resolve( [state.uploadUrl, state.uploadUrls] );
            })
            // If we did not receive an endpoint from Helios 2.
            .fail(fail => {
              // Not sure what we can do here, for now just log an error message and reject promise.
              const error = 'ERROR: no video upload endpoint could be retrieved. Upload could not be started.';
              console.log(error);
              console.log(fail);

              reject(error);
            });

          // Otherwise use endpoint we have already been given.
        } else {
          // Resolve promise.
          resolve( [state.uploadUrl, state.uploadUrls] );
        }
      });
    };

    /**
     * Upload a video.
     */
    var uploadVideo = function(
      videoInput,
      uploadUrl, // This could contain an upload URL for Google API
      uploadFile,
      progressBarEl,
      progressTextEl,
      helpLabel,
      helpContainer,
      errorLabel,
      uploadUrls // This could contain an array of upload URLs for AWS api
    ) {

      // If we use old (Google) API
      if (!uploadUrls) {
        // Create a new File object to pass to UpChunk (if supported), by doing this we can correctly set the file type for unsupported
        // file types on the OS.
        var file;
        if (!navigator.msSaveBlob) {
          file = new File([uploadFile.slice(0, uploadFile.size)], state.uploadFilename, {
            type: state.uploadFileType,
          });
          // Otherwise fallback to File object extracted directly from file field (Edge, IE11).
        } else {
          file = uploadFile;
        }

        // Create UpChunk instance.
        const upload = createUpload({
          endpoint:  uploadUrl,
          file:      file,
          chunkSize: config.chunkSize,
          attempts:  3,
        });

        // Update help label.
        helpLabel.innerText = NGX.lang.form.upload.video.pending;

        // Monitor progress.
        upload.on('progress', progress => {
          handleProgress(progress, progressBarEl, progressTextEl);
        });

        // Listen for success event.
        upload.on('success', () => {
          handleSuccess(helpLabel, state.uploadToken);
        });

        // Listen for error event.
        upload.on('error', err => {
          handleError(err.detail, videoInput, upload, helpLabel, helpContainer, errorLabel);
        });
      } else { // We use new (AWS) API
        // We need to split the file for 5Mb chunks, and upload them to appropriate URLs. We also need to gather
        // ETag headers from responses to pass them later to helios.
        uploadVideoAWS(uploadUrls, uploadFile, progressBarEl, progressTextEl, helpLabel, videoInput, helpContainer, errorLabel);
      }
    };

    const uploadVideoAWS = function(uploadUrls, uploadFile, progressBarEl, progressTextEl, helpLabel, videoInput, helpContainer, errorLabel) {
      let currentProgress = 0;
      let progressStep = Math.round(100 / uploadUrls.length);
      let offset = 0;

      const requests = uploadUrls.map( (presignedUrl, chunkOrderNum) => {
        const chunk = uploadFile.slice(offset, Math.min(offset + config.awsChunkSize, uploadFile.size));
        offset += config.awsChunkSize;
        const uploadPromise = uploadPart(chunk, presignedUrl, chunkOrderNum + 1);
        uploadPromise.then( success => {
          // Here we can update progress
          currentProgress += 1;
          let updatedProgressValue = Math.round(currentProgress * progressStep);
          // This hack is used when we have float value, like 99.667, to ensure that we will render 100%
          if (updatedProgressValue >= 98) {
            updatedProgressValue = 100;
          }
          handleProgress(
            { detail: updatedProgressValue },
            progressBarEl,
            progressTextEl
          );
        });
        return uploadPromise;
      });

      // Waiting until all requests to presigned URLs will be completed.
      Promise.all(requests)
        .then( success => {
          const eTags = success.map(resp => resp.eTag).join(',');
          // Notifying Helios about the sucessful upload into AWS
          $.ajax({
            type: 'GET',
            url:  NGX.App.config.helios2URLSecure + '/api/interact/d/video/completeUpload',
            data: {
              apikey:        _cntx.apikey,
              campaignId:    NGX.App.config.id,
              eTags:         eTags,
              token:         state.uploadToken,
              uploadId:      state.uploadId
            },
            dataType: 'json',
          })
            // If Helios successfully handled the completeUpload request
            .success(response => {
              state.uploadToken = response.token;
              handleSuccess(helpLabel, state.uploadToken);
            })
            // If Helios failed to handle completeUpload request
            .fail(err => {
              handleError(err, videoInput, null, helpLabel, helpContainer, errorLabel);
            });
        })
        // If somwthing went wrong during AWS uploads, we can make an attempt to retry
        .catch(err => {
          handleError(err, videoInput, null, helpLabel, helpContainer, errorLabel);
        });
    }

    // This function is to upload the chunk to AWS API
    const uploadPart = function(fileChunk, presignedUrl, partNo) {
      return new Promise( (resolve) => {
        fetch(presignedUrl, {
          method: 'PUT',
          body: fileChunk,
        }).then( resp => {
          resolve( {
            eTag:   resp.headers.get('ETag') ? resp.headers.get('ETag') : '',
            partNo: partNo
          } );
        });
      });
  }

    var handleProgress = function(progress, progressBarEl, progressTextEl) {
      const rounded = Math.round(progress.detail);
      if (rounded > 0 && rounded <= 100) {
        // Update bar width and text.
        progressBarEl.style.width = rounded + '%';
        progressTextEl.innerText = rounded + '%';

        // Invert text if bar over half way.
        if (rounded >= 55) {
          progressTextEl.classList.add('xInvertText');
        }
      }
    };

    var handleSuccess = function(helpLabel, uploadToken) {
      // Update help label.
      helpLabel.innerText = NGX.lang.form.upload.video.success;

      // Store upload token in a hidden field on the form so that BE can make the association
      // between the uploaded file and the entry.
      NGX.App.api.setFormField('ngxVideoToken', uploadToken, 'hidden');

      // Re-enable submit button after uploading has finished.
      enableSubmit();
    };

    var handleError = function(error, videoInput, upload, helpLabel, helpContainer, errorLabel) {
      // List of reponse codes we can auto retry using an exponential backoff algorithm.
      const autoRetryResponseCodes = ['429'];

      var canRety = false;
      for (const code of autoRetryResponseCodes) {
        if (error.message?.indexOf(code) !== -1) {
          canRety = true;
          break;
        }
      }

      // Retry using exponential backoff algorithm.
      if (canRety) {
        const retryDelay = config.backoffDelay * state.backoffIndex;

        setTimeout(() => {
          retryUpload(upload, helpLabel, helpContainer, errorLabel);
        }, retryDelay);

        state.backoffIndex++;

        // If we can't handle this error automatically, show error message and prompt user.
      } else {
        // Reset backoff index.
        state.backoffIndex = 1;

        // Show error.
        showError(helpLabel, helpContainer, errorLabel);

        // Show retry button.
        renderRetry(videoInput, upload, helpLabel, helpContainer, errorLabel);
      }
    };

    /**
     * Show/hide video upload error.
     */
    var showError = function(helpLabel, helpContainer, errorLabel) {
      // Hide help message.
      helpLabel.classList.add('xHidden');

      // Add error class to help container.
      helpContainer.classList.add('xFieldError');

      // Update error label.
      errorLabel.innerText = NGX.lang.form.upload.video.error + ' ';
    };

    var hideError = function(helpLabel, helpContainer, errorLabel) {
      // Show help message.
      helpLabel.classList.remove('xHidden');

      // Remove error class to help container.
      helpContainer.classList.remove('xFieldError');

      // Update error label.
      errorLabel.innerText = '';
    };

    var resetMessages = function(helpLabel, helpContainer, errorLabel) {
      // Update help label.
      helpLabel.innerText = state.initialHelp ? state.initialHelp : '';

      // Update error labels
      if (state.noDefaultError) {
        errorLabel.innerText = '';
      }

      // Show help message.
      helpLabel.classList.remove('xHidden');
    };

    /**
     * Render video upload progress bar.
     */
    var renderProgress = function(videoInput) {
      // Prefix for all element classnames.
      const classPrefix = config.progressClassPrefix;

      // The parent field container element.
      const parentEl = $(videoInput).closest('.xFieldContainer')[0];

      // Remove existing element if present.
      const uploadProgressContainer = $(parentEl).find('.' + classPrefix + 'Container');
      if (uploadProgressContainer.length > 0) {
        parentEl.removeChild(uploadProgressContainer[0]);
      }

      // The outer container.
      const progressBarContainerEl = document.createElement('div');
      progressBarContainerEl.classList.add(classPrefix + 'Container');

      // The inner wrapper.
      const progressBarWrapperEl = document.createElement('div');
      progressBarWrapperEl.classList.add(classPrefix + 'Wrapper');

      // The bar itself.
      const progressBarEl = document.createElement('div');
      progressBarEl.classList.add(classPrefix + 'Bar');

      // The percentage text.
      const progressTextEl = document.createElement('div');
      progressTextEl.classList.add(classPrefix + 'Text');

      // Append bar to dom.
      progressBarWrapperEl.appendChild(progressBarEl);
      progressBarWrapperEl.appendChild(progressTextEl);
      progressBarContainerEl.appendChild(progressBarWrapperEl);
      const fieldEl = $(videoInput).closest('.xField')[0];
      fieldEl.parentNode.insertBefore(progressBarContainerEl, fieldEl.nextSibling);

      return [progressBarContainerEl, progressBarEl, progressTextEl];
    };

    var destroyProgress = function(videoInput) {
      // Prefix for all element classnames.
      const classPrefix = config.progressClassPrefix;

      // The parent field container element.
      const parentEl = $(videoInput).closest('.xFieldContainer')[0];

      // Remove existing element if present.
      const uploadProgressContainer = $(parentEl).find('.' + classPrefix + 'Container');
      if (uploadProgressContainer.length > 0) {
        parentEl.removeChild(uploadProgressContainer[0]);
      }
    };

    /**
     * Render video upload retry button.
     */
    var renderRetry = function(videoInput, uploadInstance, helpLabel, helpContainer, errorLabel) {
      // Create retry button.
      const retryEl = document.createElement('a');
      retryEl.classList.add('xVideoUploadRetry');
      retryEl.innerText = NGX.lang.form.upload.video.retry;

      // Attach click handler.
      retryEl.onclick = e => {
        e.preventDefault();

        retryUpload(uploadInstance, helpLabel, helpContainer, errorLabel, videoInput);
      };

      errorLabel.appendChild(retryEl);
    };

    var retryUpload = function(uploadInstance, helpLabel, helpContainer, errorLabel, videoInput) {
      // Expire existing url.
      state.uploadUrlExpired = true;

      // Retrieve new url and resume upload.
      getUploadUrl(state.uploadFileType).then( ([uploadUrl, uploadUrls]) => {

        // If we deal with old API (Google)
        if (!uploadUrls) {
          // Set new upload url.
          uploadInstance.endpointValue = uploadUrl;

          // Resume.
          uploadInstance.pause();
          uploadInstance.resume();
        } else {
          // Rendering progressBar
          const [progressBarContainerEl, progressBarEl, progressTextEl] = renderProgress(videoInput);
          // Working with new (AWS) API
          uploadVideoAWS(uploadUrls, state.uploadFile, progressBarEl, progressTextEl, helpLabel, videoInput, helpContainer, errorLabel);
        }
        // Hide the error and re-show the help message.
        hideError(helpLabel, helpContainer, errorLabel);
      });
    };

    /**
     * Toggle the form submit button.
     */
    var disableSubmit = function() {
      // Get handle on button and related elements.
      const submitButton = $('.xSubmit');
      const submitContainer = submitButton.closest('.xActivateContainer');
      const submitContainerEl = submitContainer[0];
      const submitButtonEl = submitButton[0];
      const submitTextEl = submitButton.find('span')[0];

      // Add classnames.
      submitButton.attr('disabled', 'disabled').addClass('xDisabled xActivity');

      // Append xIcon element to button.
      const icon = document.createElement('span');
      icon.classList.add('xIcon');
      submitButtonEl.insertBefore(icon, submitTextEl);

      // Append the status message below submit button.
      const statusMessage = document.createElement('div');
      statusMessage.classList.add('xStatusMessage');
      statusMessage.innerText = NGX.lang.form.upload.video.formStatus;
      submitContainerEl.appendChild(statusMessage);

      // Resize form.
      setTimeout(formUtil.formWrapperResize, 500);
    };

    var enableSubmit = function() {
      // Get handle on button and related elements.
      const submitButton = $('.xSubmit');
      const submitContainer = submitButton.closest('.xActivateContainer');
      const submitContainerEl = submitContainer[0];
      const submitButtonEl = submitButton[0];

      // Remove classnames.
      submitButton.attr('disabled', null).removeClass('xDisabled xActivity');

      // Remove xIcon element from button.
      const submitIcon = submitButton.find('.xIcon')[0];
      if (submitIcon) {
        submitButtonEl.removeChild(submitIcon);
      }

      // Remove status message.
      const statusMessage = submitContainer.find('.xStatusMessage')[0];
      if (statusMessage) {
        submitContainerEl.removeChild(statusMessage);
      }

      // Resize form.
      setTimeout(formUtil.formWrapperResize, 500);
    };

    return {
      init: init,
    };
  })();

  return {
    init,
    renderMultiOptIns,
    initialiseFormStyling,
    validateCaptcha,
    submitCampaignObjectForm,
    checkReturningUser,
    registerCustomSubmitHandler,
    bindAPItriggers,
  };
})();
